// Phase G3 Stage 1b: /play/recap — post-session celebration.
// T1.3.1: ?correct=&total= → real stars (attempt accuracy).
// T1.4a: + ?pre= = pre-session mastered-skill count (captured at session
// mount via aggregateSkillStates). Drives the skills-mastered tile's
// delta — tile renders only when post > pre (real growth this session).
// P-MILESTONE-DERIVATION: + ?subject=&mastery= carry WHICH trail strand
// this session completed and whether it cleared the mastery tier, so the
// recap can fill the just-added "page" (and gold-accent it on mastery)
// WITHOUT waiting on the fire-and-forget session_completed marker to commit.
//
// UX-DILIGENCE G3 — param guard: /play/recap with NO meaningful session
// params (no `total`, no `subject`) renders a blank, confusing screen.
// Redirect such reaches to /play so the kid/parent lands somewhere
// sensible. Exception: `?dev=1` bypasses the guard (e2e + dev-server
// direct navigation requires this — real sessions always carry at least
// `total` or `subject`).
import { createFileRoute, redirect } from "@tanstack/react-router";
import { SessionRecap } from "@/screens/SessionRecap";
// MA-928 — the turn hand-off on an armed class door device (renders nothing otherwise).
import { KioskHandoff, KIOSK_DOOR_PATH, useKioskDoorDevice } from "@/components/KioskHandoff";
import type { TrailSubject } from "@/lib/engine/trailState";

function toNum(v: unknown): number | undefined {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function toSubject(v: unknown): TrailSubject | undefined {
  return v === "math" || v === "reading" || v === "mixed" ? v : undefined;
}

// Search carries booleans as the strings "1"/"0" (URLSearchParams has no
// native boolean); treat exactly "1" / true as mastery, everything else false.
function toBool(v: unknown): boolean | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  return v === "1" || v === 1 || v === true || v === "true";
}

export const Route = createFileRoute("/play/recap")({
  // UX-DILIGENCE G3 — redirect bare/invalid reaches to /play.
  // A real session always arrives with at least `total` (from the
  // session's answer count) or `subject` (from which trail strand
  // just completed). Neither present AND no dev bypass → stale
  // reload or direct-link: send to the trail home.
  beforeLoad: ({ search }) => {
    const s = search as Record<string, unknown>;
    const hasSession =
      (s.total !== undefined && s.total !== null && s.total !== "") ||
      (s.subject !== undefined && s.subject !== null && s.subject !== "");
    const isDev = s.dev === "1" || s.dev === 1;
    if (!hasSession && !isDev) {
      throw redirect({ to: "/play" });
    }
  },
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    correct?: number;
    total?: number;
    pre?: number;
    subject?: TrailSubject;
    mastery?: boolean;
    short?: boolean;
    skillId?: string;
    runId?: string;
  } => ({
    correct: toNum(search.correct),
    total: toNum(search.total),
    pre: toNum(search.pre),
    subject: toSubject(search.subject),
    mastery: toBool(search.mastery),
    short: toBool(search.short),
    // MA-624 — the focal skill + run id of the just-completed session, so
    // SessionRecap's DifficultyFeedback can attribute its nudge. Optional:
    // absent on a direct/param-less reach, or from a screen with no single
    // focal skill (Mixed).
    skillId: typeof search.skillId === "string" ? search.skillId : undefined,
    runId: typeof search.runId === "string" ? search.runId : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { correct, total, pre, subject, mastery, short, skillId, runId } = Route.useSearch();
  // MA-928 (D-1013-23) — on a class door device the recap's /play exits go to the door, replacing history.
  const door = useKioskDoorDevice();
  return (
    <>
      <SessionRecap
        correct={correct}
        total={total}
        pre={pre}
        subject={subject}
        mastery={mastery}
        short={short}
        skillId={skillId}
        runId={runId}
        homeTo={door ? KIOSK_DOOR_PATH : undefined}
        homeReplace={door ? true : undefined}
      />
      <KioskHandoff allDone />
    </>
  );
}
